/**
 * Turn any Axios/fetch error into a user-safe message string.
 *
 * The Admin Send-now UI previously rendered `[object Object]` when FastAPI
 * returned a Pydantic-validation `detail` (an ARRAY of `{type,loc,msg,...}`
 * objects). Passing that through `String(detail)` on an array coerces every
 * object to `"[object Object]"`, joined by commas — never useful, never
 * safe, and never something we can show a customer or operator.
 *
 * This helper:
 *   - Recognises FastAPI validation-error arrays and extracts the first msg
 *   - Maps well-known backend error strings to friendly copy
 *   - Falls back to a generic "The request could not be completed." string
 *   - NEVER returns the literal "[object Object]"
 *
 * The known-error map is scoped to send / delivery flows so we only surface
 * copy that is meaningful in that context. Add new mappings sparingly.
 */

// Known backend `detail` substrings → safe, action-oriented copy.
// The keys are matched via `.includes()` on the raw detail string, so they
// stay resilient to minor phrasing changes in the backend.
const KNOWN_ERROR_MAP = [
  {
    match: "EMAIL_SEND_ENABLED is false",
    safe: "Production sending is disabled.",
  },
  {
    match: "not the approved rendering",
    safe: "The rendering must be approved before sending.",
  },
  {
    match: "Approve it first",
    safe: "The rendering must be approved before sending.",
  },
  {
    match: "paused",
    safe: "The recipient is paused or unsubscribed.",
  },
  {
    match: "unsubscribed",
    safe: "The recipient is paused or unsubscribed.",
  },
  {
    match: "EMAIL_TEST_ALLOWLIST",
    safe: "Test sends must go to an address on the allowlist.",
  },
  {
    match: "FINGERPRINT_MISMATCH",
    safe: "The rendering has changed since it was approved. Re-approve and try again.",
  },
  {
    match: "Provider error",
    safe: "The email provider rejected the request.",
  },
  {
    match: "Rendering not found",
    safe: "That rendering no longer exists.",
  },
  {
    match: "Delivery cannot be canceled",
    safe: "This delivery is already in flight and cannot be canceled.",
  },
  {
    match: "already terminal",
    safe: "That delivery has already completed.",
  },
];

const GENERIC_MESSAGE = "The request could not be completed. Please try again.";

/**
 * Extract a plain, non-empty string from an unknown `detail` value.
 * Returns `null` if nothing useful can be extracted.
 */
function extractDetailString(detail) {
  if (detail == null) return null;
  if (typeof detail === "string") return detail.trim() || null;

  // FastAPI validation errors: array of `{ msg, loc, type, ... }`
  if (Array.isArray(detail)) {
    for (const item of detail) {
      if (typeof item === "string" && item.trim()) return item.trim();
      if (item && typeof item === "object") {
        const msg = item.msg || item.message || item.detail;
        if (typeof msg === "string" && msg.trim()) return msg.trim();
      }
    }
    return null;
  }

  // Some backends return `{ message: "..." }` or `{ detail: "..." }`
  if (typeof detail === "object") {
    const nested = detail.message || detail.detail || detail.error;
    if (typeof nested === "string" && nested.trim()) return nested.trim();
  }
  return null;
}

export function formatApiErrorForToast(err, fallback = GENERIC_MESSAGE) {
  const detail = err?.response?.data?.detail;
  const raw = extractDetailString(detail) || extractDetailString(err?.message);

  if (raw) {
    // Map known signals to safe, action-oriented copy first.
    for (const { match, safe } of KNOWN_ERROR_MAP) {
      if (raw.toLowerCase().includes(match.toLowerCase())) return safe;
    }
    // Guardrail: never let "[object Object]" through.
    if (raw !== "[object Object]") return raw;
  }
  return fallback;
}
